import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
    fromScopedKey,
    MenuChange,
    MenuChangeSchema,
    ModifierChangeUpdate,
    ModifierOptionChange,
    toModifierProductKey
} from '@pepperhq/menu-sdk';
import { menuApi } from 'components/menu/MenuApi';
import { MuiGrid } from 'lib/MuiGrid/MuiGrid';
import React, { useContext } from 'react';
import {
    BaseMenuTab,
    getMenuChangeDataToCopy,
    getModifierIdModifierOptions,
    getModifiersData,
    isModifiersMinMaxSelectSupported,
    ModifierOptionsMap,
    useMenuChange
} from '../model/menu';
import { useLeavePageBlock } from 'lib/hooks/useLeavePageBlock';
import logger from 'lib/logger';
import { useSnackbar } from 'notistack';
import { MESSAGE_MODIFIERS_UPDATE_ERROR, MESSAGE_MODIFIERS_UPDATE_SUCCESS } from 'config/messages';
import { SelectedModifierGridColumn } from '../selectedModifierSelect';
import merge from 'deepmerge';
import { useMenuGridSearch } from '../model/useMenuGridSearch';
import { GridStorageName } from 'lib/MuiGrid/StateController';
import { isEmptyString } from 'lib/typeguards';
import { LocationsContext, useTabCopy } from '../useCopyTab';
import { ActionsHeader } from '../ActionsHeader';
import { EPointOfSaleProvider } from 'components/settings/PointOfSaleSettings';
import { useMenuSelection } from '../useMenuSelection';
import { useMenuDeleteControls } from '../model/useMenuDeleteControls';

type ModifiersTabProps = BaseMenuTab;

export const ModifiersTab: React.FC<ModifiersTabProps> = ({
    menu,
    settings,
    menuChangeId,
    loadFreshMenu,
    onUpdate,
    change: externalChange,
    search,
    onSearchChange
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const locations = useContext(LocationsContext);
    const {
        change: [change, setChange],
        readyToSave,
        handleCancel,
        handleStatusChange,
        gridRef
    } = useMenuChange(externalChange);

    const isMixMaxSupported = React.useMemo(() => isModifiersMinMaxSelectSupported(settings), [settings]);

    const includeFieldsToCopy = React.useMemo(() => {
        let fields = ['title', 'bundle', 'allowQuantitySelect', 'hide', 'isCollapsed'];
        if (isMixMaxSupported) {
            fields = [...fields, 'minSelect', 'maxSelect'];
        }
        return fields;
    }, [isMixMaxSupported]);

    const {
        selectedIds,
        resetSelection,
        selectedCell,
        handleSelectChange,
        handleCellSelection,
        copyEnabled,
        handleCellBlur
    } = useMenuSelection(!!locations.length);

    const { toggleIsCopyModalOpen, setMenuChangeToCopy, activeOperationId, renderLocationPickerModal } =
        useTabCopy(resetSelection, selectedCell, includeFieldsToCopy);

    const handleRefreshMenu = React.useCallback(() => {
        loadFreshMenu();
        gridRef.current.reset();
    }, [gridRef, loadFreshMenu]);

    const modifiers = React.useMemo(() => {
        if (!menu || !change) {
            return [];
        }
        return getModifiersData(menu, change, settings.posProvider);
    }, [change, menu, settings.posProvider]);

    const modifierIdModifierOptions = React.useMemo<ModifierOptionsMap>(() => {
        if (!menu || !change) {
            return new Map();
        }
        return getModifierIdModifierOptions(menu, change, settings.posProvider);
    }, [menu, change, settings.posProvider]);

    const {
        editedOrDeleted,
        handleCancelEditing,
        handleDelete,
        deleted,
        deleteEnabled,
        resetDeleted,
        isCellEditable
    } = useMenuDeleteControls(selectedIds, readyToSave, handleCancel, resetSelection);

    useLeavePageBlock(editedOrDeleted);
    const gridSearchOptions = React.useMemo(() => [{ column: 'id' }, { column: 'title' }], []);
    const { filterModel, renderInput } = useMenuGridSearch(
        gridSearchOptions,
        search,
        onSearchChange,
        'modifiers',
        'Search by ID or title'
    );

    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: 'id',
                headerName: 'ID',
                width: 120
            },
            {
                field: 'title',
                headerName: 'Title',
                editable: true,
                width: 300
            },
            {
                field: 'category',
                headerName: 'Category',
                width: 200
            },
            {
                field: 'hide',
                headerName: 'Hidden',
                width: 140,
                editable: true,
                headerAlign: 'left',
                type: 'boolean'
            },
            {
                field: 'minSelect',
                headerName: 'Minimum Selection',
                type: 'number',
                width: 220,
                editable: isMixMaxSupported,
                headerAlign: 'left'
            },
            {
                field: 'maxSelect',
                headerName: 'Maximum Selection',
                type: 'number',
                width: 220,
                editable: isMixMaxSupported,
                headerAlign: 'left'
            },
            {
                field: 'isCollapsed',
                headerName: 'Collapsed',
                width: 140,
                editable: true,
                headerAlign: 'left',
                type: 'boolean',
                description:
                    'Determines whether the modifier group will be open or closed when landing on the product details page'
            },
            {
                field: 'bundle',
                headerName: 'Bundle',
                width: 140,
                editable: true,
                headerAlign: 'left',
                type: 'boolean'
            },
            {
                field: 'allowQuantitySelect',
                headerName: 'Quantity Select',
                width: 200,
                editable: settings.posProvider !== EPointOfSaleProvider.SQUARE,
                headerAlign: 'left',
                description:
                    settings.posProvider !== EPointOfSaleProvider.SQUARE ? undefined : 'Not supported',
                type: 'boolean'
            },
            {
                field: 'selectedOptions',
                headerName: 'Selected Product/Option',
                width: 270,
                headerAlign: 'left',
                editable: true,
                ...SelectedModifierGridColumn(modifierIdModifierOptions)
            },
            {
                field: 'displayOptions',
                headerName: 'Products/Options',
                width: 350,
                ...SelectedModifierGridColumn(modifierIdModifierOptions),
                editable: false
            }
        ],
        [isMixMaxSupported, modifierIdModifierOptions, settings.posProvider]
    );
    const handleSubmit = React.useCallback(async () => {
        try {
            if (!gridRef.current) {
                throw new Error(MESSAGE_MODIFIERS_UPDATE_ERROR);
            }
            const errors = gridRef.current.validate();
            if (errors) {
                return;
            }
            const { changes } = gridRef.current.getState();
            const modifierOptions: Record<string, ModifierOptionChange> = {};
            const modifierProducts: Record<string, ModifierOptionChange> = {};
            const modifierChanges = Object.entries(changes).reduce<MenuChange['modifiers']>(
                (acc, [modifierId, { category: _, selectedOptions, _overriden, ...item }]) => {
                    const { id, parentId } = fromScopedKey(modifierId);
                    const isModifierProducts =
                        menu.modifiers?.find(modifier => modifier.id === id)?.products ||
                        menu.categories.some(
                            category => category.modifiers?.find(modifier => modifier.id === id)?.products
                        );
                    if (Array.isArray(selectedOptions)) {
                        const options = modifierIdModifierOptions.get(id);
                        if (options) {
                            Object.keys(options).forEach(modifierOptionId => {
                                if (selectedOptions.includes(modifierOptionId)) {
                                    if (isModifierProducts) {
                                        modifierProducts[
                                            toModifierProductKey(modifierOptionId, id, parentId)
                                        ] = {
                                            selected: true
                                        };
                                    } else {
                                        modifierOptions[modifierOptionId] = {
                                            selected: true
                                        };
                                    }
                                } else if (isModifierProducts) {
                                    modifierProducts[toModifierProductKey(modifierOptionId, id, parentId)] = {
                                        selected: null
                                    };
                                } else {
                                    modifierOptions[modifierOptionId] = {
                                        selected: null
                                    };
                                }
                            });
                        }
                    } else if (
                        (Array.isArray(selectedOptions) && !selectedOptions.length) ||
                        isEmptyString(selectedOptions)
                    ) {
                        const options = modifierIdModifierOptions.get(id);
                        if (options) {
                            Object.keys(options).forEach(option => {
                                if (isModifierProducts) {
                                    modifierProducts[toModifierProductKey(option, id, parentId)] = {
                                        selected: null
                                    };
                                } else {
                                    modifierOptions[option] = {
                                        selected: null
                                    };
                                }
                            });
                        }
                    }
                    if (!change?.modifiers || !change?.modifiers[id]) {
                        acc[modifierId] = item;
                    } else {
                        acc[modifierId] = { ...change.modifiers[id], ...item };
                    }
                    if (isEmptyString(acc[modifierId].hide)) {
                        acc[modifierId].hide = null;
                    }
                    if (isEmptyString(acc[modifierId].bundle)) {
                        acc[modifierId].bundle = null;
                    }
                    if (isEmptyString(acc[modifierId].isCollapsed)) {
                        acc[modifierId].isCollapsed = null;
                    }
                    if (isEmptyString(acc[modifierId].allowQuantitySelect)) {
                        acc[modifierId].allowQuantitySelect = null;
                    }
                    if (isEmptyString(acc[modifierId].title)) {
                        acc[modifierId].title = null;
                    }
                    return acc;
                },
                {}
            );
            const additionalData: Record<string, ModifierOptionChange> = {};
            if (Object.keys(modifierProducts).length) {
                additionalData['modifierProducts'] = merge(
                    externalChange?.modifierProducts || {},
                    modifierProducts
                );
            }
            if (Object.keys(modifierOptions).length) {
                additionalData['modifierOptions'] = merge(
                    externalChange?.modifierOptions || {},
                    modifierOptions
                );
            }
            if (deleted) {
                deleted.forEach(modifiers => {
                    modifierChanges[modifiers] = null;
                });
            }
            const result = await menuApi.updateMenuChange(menuChangeId, {
                modifiers: modifierChanges,
                schema: MenuChangeSchema.v5,
                ...additionalData
            });
            if (!result.ok) {
                throw new Error(result.body.message);
            }
            setChange(result.body);
            onUpdate(result.body);
            resetDeleted();
            enqueueSnackbar(MESSAGE_MODIFIERS_UPDATE_SUCCESS, { variant: 'success' });
            gridRef.current.reset();
        } catch (e) {
            logger.error(e);
            enqueueSnackbar(e.message, { variant: 'error' });
        }
    }, [
        gridRef,
        menu.modifiers,
        menu.categories,
        externalChange?.modifierProducts,
        externalChange?.modifierOptions,
        deleted,
        menuChangeId,
        setChange,
        onUpdate,
        resetDeleted,
        change.modifiers,
        modifierIdModifierOptions,
        enqueueSnackbar
    ]);

    const handleCopyTo = React.useCallback(() => {
        toggleIsCopyModalOpen();
        const menuChange = getMenuChangeDataToCopy<ModifierChangeUpdate>(
            change,
            selectedIds.map(compoundId => {
                const { id } = fromScopedKey(compoundId);
                return id;
            }),
            'modifiers',
            selectedCell,
            row => {
                const { id } = fromScopedKey(row.id);
                return id;
            }
        );
        setMenuChangeToCopy(menuChange);
    }, [toggleIsCopyModalOpen, change, selectedIds, selectedCell, setMenuChangeToCopy]);

    return (
        <React.Fragment>
            <Box pb={1}>
                <ActionsHeader
                    menu={menu}
                    onCancelEditing={handleCancelEditing}
                    onSubmit={handleSubmit}
                    activeOperationId={activeOperationId}
                    onCopy={handleCopyTo}
                    onRefreshMenu={handleRefreshMenu}
                    isEditedOrDeleted={editedOrDeleted}
                    copyEnabled={copyEnabled}
                    onDelete={handleDelete}
                    deleteEnabled={deleteEnabled}
                />
                {renderInput()}
            </Box>
            <MuiGrid
                rows={modifiers}
                columns={columns}
                stateRef={gridRef}
                onStatusChange={handleStatusChange}
                filterModel={filterModel}
                storageName={GridStorageName.Modifiers}
                disableColumnFilter
                checkboxSelection
                checkboxVisibility
                onRowSelectionModelChange={handleSelectChange}
                rowSelectionModel={selectedIds}
                onCellClick={handleCellSelection}
                deleted={deleted}
                isCellEditable={isCellEditable}
                handleCellBlur={handleCellBlur}
            />
            {renderLocationPickerModal()}
        </React.Fragment>
    );
};
